import type Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import type VectorLayer from 'ol/layer/Vector';
import type { Pixel } from 'ol/pixel';

export interface MapPopupBounds {
    left: number;
    top: number;
    right: number;
    bottom: number;
}

export interface MapPopupPoint {
    x: number;
    y: number;
}

const MAP_POPUP_MARGIN = 8;
const MAP_POPUP_OFFSET = 9;
const MAP_POPUP_MIN_WIDTH = 206;
const MAP_POPUP_BORDER_AND_PADDING = 6;

export function getVisibleLayerFeatures(
    layer: VectorLayer | null | undefined,
    pixel: Pixel,
): Promise<Feature<Geometry>[]> {
    if (!layer?.getVisible() || !layer.hasRenderer()) {
        return Promise.resolve<Feature<Geometry>[]>([]);
    }
    return layer.getFeatures(pixel);
}

export function fitMapPopup(
    popup: HTMLDivElement,
    point: MapPopupPoint,
    mapBounds: MapPopupBounds,
    viewportBounds: MapPopupBounds,
): [number, number] | null {
    const left = Math.ceil(Math.max(mapBounds.left, viewportBounds.left) - mapBounds.left + MAP_POPUP_MARGIN);
    const top = Math.ceil(Math.max(mapBounds.top, viewportBounds.top) - mapBounds.top + MAP_POPUP_MARGIN);
    const right = Math.floor(Math.min(mapBounds.right, viewportBounds.right) - mapBounds.left - MAP_POPUP_MARGIN);
    const bottom = Math.floor(Math.min(mapBounds.bottom, viewportBounds.bottom) - mapBounds.top - MAP_POPUP_MARGIN);
    const width = right - left;
    const height = bottom - top;

    if (width <= MAP_POPUP_BORDER_AND_PADDING || height <= MAP_POPUP_BORDER_AND_PADDING) {
        return null;
    }

    popup.style.minWidth = `${Math.min(MAP_POPUP_MIN_WIDTH, width)}px`;
    popup.style.maxWidth = `${width}px`;
    popup.style.maxHeight = `${height}px`;

    const popupBounds = popup.getBoundingClientRect();
    if (popupBounds.width <= 0 || popupBounds.height <= 0 || popupBounds.width > width || popupBounds.height > height) {
        return null;
    }

    let x = point.x + MAP_POPUP_OFFSET;
    let y = point.y + MAP_POPUP_OFFSET;

    if (x + popupBounds.width > right) {
        x = point.x - MAP_POPUP_OFFSET - popupBounds.width;
    }
    if (y + popupBounds.height > bottom) {
        y = point.y - MAP_POPUP_OFFSET - popupBounds.height;
    }

    x = Math.max(left, Math.min(Math.round(x), Math.floor(right - popupBounds.width)));
    y = Math.max(top, Math.min(Math.round(y), Math.floor(bottom - popupBounds.height)));

    return [x - point.x, y - point.y];
}
